import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useTickerSearch } from '../../hooks/useTickerSearch';
import { isBaseFuturesProduct, getCurrentContractSymbol, getNextContractMonth, resolveTradableSymbol } from '../../utils/futuresContracts';
import FormInput from './FormInput';

function typeColor(type) {
  switch (String(type || '').toLowerCase()) {
    case 'futures': return '#FFA500';
    case 'etf': return '#00BFFF';
    case 'stock': return '#32CD32';
    default: return '#7B8A9E';
  }
}

export default function TickerSearchInput({
  value = '',
  onChange,
  placeholder = 'MES',
  ariaLabel = 'Symbol',
  width = 84,
  height = 32,
  style,
}) {
  const { colors } = useTheme();
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const [draft, setDraft] = useState(String(value || '').toUpperCase());
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [anchor, setAnchor] = useState(null);
  const results = useTickerSearch(open ? draft : '', { enabled: open });

  useEffect(() => {
    if (!open) setDraft(String(value || '').toUpperCase());
  }, [open, value]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [draft]);

  const updateAnchor = useCallback(() => {
    if (!wrapRef.current) return;
    setAnchor(wrapRef.current.getBoundingClientRect());
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return undefined;
    }
    updateAnchor();
    window.addEventListener('resize', updateAnchor);
    window.addEventListener('scroll', updateAnchor, true);
    return () => {
      window.removeEventListener('resize', updateAnchor);
      window.removeEventListener('scroll', updateAnchor, true);
    };
  }, [open, draft, updateAnchor]);

  const commit = useCallback((symbol) => {
    const next = resolveTradableSymbol(symbol);
    if (!next) return;
    setDraft(next);
    setOpen(false);
    if (next !== value) onChange?.(next);
  }, [onChange, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (listRef.current?.contains(event.target)) return;
      commit(draft);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [commit, draft, open]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setSelectedIndex((prev) => Math.min(prev + 1, Math.max(0, results.length - 1)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, -1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        commit(results[selectedIndex].symbol);
      } else {
        commit(draft);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setDraft(String(value || '').toUpperCase());
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width }}>
      <FormInput
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls="ticker-search-results"
        value={draft}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          setDraft(String(e.target.value || '').toUpperCase());
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          updateAnchor();
        }}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%',
          height,
          fontWeight: 700,
          letterSpacing: '0.04em',
          ...style,
        }}
      />
      {open && anchor
        ? createPortal(
          <div
            id="ticker-search-results"
            ref={listRef}
            role="listbox"
            data-testid="ticker-search-results"
            style={{
              position: 'fixed',
              top: anchor.bottom + 4,
              left: anchor.left,
              width: Math.max(280, anchor.width),
              maxHeight: 280,
              overflowY: 'auto',
              zIndex: 4000,
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
            }}
          >
            {results.length === 0 ? (
              <div style={{ padding: '10px 12px', color: colors.textMuted, fontSize: 12 }}>
                {draft.trim() ? `Press Enter to use ${draft.trim().toUpperCase()}` : 'Type to search'}
              </div>
            ) : results.map((result, index) => {
              const active = index === selectedIndex;
              const futuresBase = isBaseFuturesProduct(result.symbol);
              const contract = futuresBase ? getCurrentContractSymbol(result.symbol) : null;
              const contractInfo = futuresBase ? getNextContractMonth() : null;
              return (
                <div
                  key={`${result.symbol}-${index}`}
                  role="option"
                  aria-selected={active}
                  data-testid={`ticker-search-option-${result.symbol}`}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    commit(result.symbol);
                  }}
                  style={{
                    padding: '9px 12px',
                    cursor: 'pointer',
                    background: active ? `${colors.primary}22` : 'transparent',
                    borderBottom: index < results.length - 1 ? `1px solid ${colors.border}` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: colors.text, fontWeight: 700, fontSize: 13, letterSpacing: '0.03em' }}>
                      {result.symbol}
                    </span>
                    <span
                      style={{
                        background: typeColor(result.type),
                        color: '#111',
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '2px 6px',
                        borderRadius: 4,
                        letterSpacing: '0.04em',
                      }}
                    >
                      {String(result.type || 'stock').toUpperCase()}
                    </span>
                    {contract ? (
                      <span style={{ fontSize: 10, color: colors.textMuted }}>→ {contract}</span>
                    ) : null}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: 11, marginTop: 2 }}>
                    {result.name}
                    {contractInfo ? ` (${contractInfo.monthName} ${contractInfo.year} contract)` : ''}
                  </div>
                </div>
              );
            })}
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
